import type { Vehicle, VehicleDerivative } from '@/features/vehiclesearch';

export interface VehicleIdentity {
    vin?: string;
    vrm?: string;
}

export type VehicleIdentityGateState =
    | { kind: 'satisfied' }
    | { kind: 'missing'; vehicle: Vehicle }
    | { kind: 'unrepairable' };

// A non-empty string, not `!value`: `!value` would miss `[]` (an empty CarWeb element) and
// whitespace, both of which the server treats as absent.
const presentString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() !== '' ? value : undefined;

const hasVehicleIdentity = (vehicle: Vehicle): boolean =>
    presentString(vehicle.vin) !== undefined || presentString(vehicle.current_status?.vrm) !== undefined;

// A null/absent or non-vehicle document resolves to unrepairable, never satisfied — fail
// closed, not fail open.
export const resolveVehicleIdentityGate = (
    vehicle: Vehicle | VehicleDerivative | null | undefined,
): VehicleIdentityGateState => {
    if (!vehicle || vehicle.type !== 'vehicle') {
        return { kind: 'unrepairable' };
    }
    return hasVehicleIdentity(vehicle) ? { kind: 'satisfied' } : { kind: 'missing', vehicle };
};

// Additive only: an omitted input never clears an identifier already on the vehicle.
export const applyVehicleIdentity = (vehicle: Vehicle, identity: VehicleIdentity): Vehicle => ({
    ...vehicle,
    vin: identity.vin ?? vehicle.vin,
    current_status: {
        ...vehicle.current_status,
        vrm: identity.vrm ?? vehicle.current_status?.vrm,
    },
});
